import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { commandCenterApi } from '../api';
import { CommandWebSocket } from '../ws';
import { useCommandCenter } from '../store';
import { KpiStrip } from '../components/KpiStrip';
import { LeftRail } from '../components/LeftRail';
import { AssetMap } from '../components/AssetMap';
import { AlertFeed } from '../components/AlertFeed';
import { DetailPanel } from '../components/DetailPanel';
import { TimeMachine } from '../components/TimeMachine';
import { CommandPalette } from '../components/CommandPalette';

export function CommandCenterPage() {
  const setAssets = useCommandCenter((s) => s.setAssets);
  const setAlerts = useCommandCenter((s) => s.setAlerts);
  const setGeofences = useCommandCenter((s) => s.setGeofences);
  const applyFix = useCommandCenter((s) => s.applyFix);
  const upsertAlert = useCommandCenter((s) => s.upsertAlert);
  const setConnected = useCommandCenter((s) => s.setConnected);
  const rightPanel = useCommandCenter((s) => s.rightPanel);

  // Bootstrap REST queries
  const snapshotQ = useQuery({
    queryKey: ['cc-snapshot'],
    queryFn: () => commandCenterApi.snapshot(),
    refetchOnWindowFocus: false,
  });
  const alertsQ = useQuery({
    queryKey: ['alerts', 'open'],
    queryFn: () => commandCenterApi.listAlerts({ limit: 200 }),
    refetchOnWindowFocus: false,
  });
  const geofencesQ = useQuery({
    queryKey: ['geofences'],
    queryFn: () => commandCenterApi.listGeofences(),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (snapshotQ.data) setAssets(snapshotQ.data.assets);
  }, [snapshotQ.data, setAssets]);
  useEffect(() => {
    if (alertsQ.data) setAlerts(alertsQ.data);
  }, [alertsQ.data, setAlerts]);
  useEffect(() => {
    if (geofencesQ.data) setGeofences(geofencesQ.data);
  }, [geofencesQ.data, setGeofences]);

  // Live websocket bridges
  const fleetWs = useMemo(() => new CommandWebSocket('fleet'), []);
  const alertsWs = useMemo(() => new CommandWebSocket('alerts'), []);

  useEffect(() => {
    fleetWs.connect();
    alertsWs.connect();
    setConnected(true);
    const offFleet = fleetWs.on((msg) => {
      const p = msg.payload as {
        type?: string;
        asset_id?: string;
        lat?: number;
        lng?: number;
        heading?: number | null;
        speed_kmh?: number | null;
        recorded_at?: string;
        status?: string;
      };
      if (p?.type === 'gps.fix' && p.asset_id) {
        applyFix({
          asset_id: p.asset_id,
          lat: p.lat!,
          lng: p.lng!,
          heading: p.heading ?? null,
          speed_kmh: p.speed_kmh ?? null,
          recorded_at: p.recorded_at ?? new Date().toISOString(),
          status: (p.status as never) ?? undefined,
        });
      }
    });
    const offAlerts = alertsWs.on((msg) => {
      const p = msg.payload as { type?: string; alert?: Record<string, unknown> };
      if (p?.alert && (p.alert as { id?: string }).id) {
        upsertAlert(p.alert as never);
      }
    });
    return () => {
      offFleet();
      offAlerts();
      fleetWs.close();
      alertsWs.close();
      setConnected(false);
    };
  }, [fleetWs, alertsWs, applyFix, upsertAlert, setConnected]);

  return (
    <div className="cc-root grid h-screen grid-cols-[18%_1fr_18%] grid-rows-[50px_1fr_60px] bg-cc-bg text-cc-fg">
      <div className="col-span-3">
        <KpiStrip />
      </div>

      <LeftRail />
      {/* `isolate` confines Leaflet's internal stacking context (its panes and
          controls use z-index up to 1000) to this section. Without it those
          z-indexes leak into the root stacking context and paint the map over
          fixed UI such as the logout confirmation modal and the header's
          "More" dropdown. */}
      <section className="relative isolate h-full overflow-hidden">
        <AssetMap />
      </section>
      {rightPanel === 'detail' ? <DetailPanel /> : <AlertFeed />}

      <div className="col-span-3">
        <TimeMachine />
      </div>

      <CommandPalette />
    </div>
  );
}
